import { Box, MenuItem, Select, useMediaQuery, useTheme } from '@mui/material';
import React, { ReactElement } from 'react';
import type { AttendeeSort } from 'services/api';
import { AnalyticsDateFilterTrigger } from 'components/pages/admin/analytics/AnalyticsDateFilterTrigger';
import { ExportButton } from 'components/ui';
import type { DateFilterPreset } from 'hooks/useAnalyticsDateFilter';
import { PeopleSearchInput } from '../PeopleSearchInput';

const BORDER_COLOR = '#DEDEDE';
const TEXT_COLOR = '#383838';

export interface AttendeesToolbarProps {
  sort: AttendeeSort;
  onSortChange: (sort: AttendeeSort) => void;
  search: string;
  onSearchChange: (value: string) => void;
  dateFilterPreset: DateFilterPreset;
  dateFilterStartDate: Date | null;
  dateFilterEndDate: Date | null;
  onOpenDateFilter: () => void;
  onExport: () => void;
  exporting?: boolean;
}

const SORT_OPTIONS: { label: string; value: AttendeeSort }[] = [
  { label: 'Engagement', value: 'engagement' },
  { label: 'Last Attended', value: 'lastAttended' },
  { label: 'Name', value: 'name' },
];

export function AttendeesToolbar ({
  sort,
  onSortChange,
  search,
  onSearchChange,
  dateFilterPreset,
  dateFilterStartDate,
  dateFilterEndDate,
  onOpenDateFilter,
  onExport,
  exporting = false,
}: AttendeesToolbarProps): ReactElement {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const sortSelect = (
    <Select
      value={sort}
      onChange={e => onSortChange(e.target.value as AttendeeSort)}
      renderValue={value => {
        const label = SORT_OPTIONS.find(o => o.value === value)?.label ?? '';
        return `Sort by ${label}`;
      }}
      sx={{
        height: 50,
        width: { xs: '100%', md: 250 },
        borderRadius: '8px',
        fontFamily: '\'Poppins\', sans-serif',
        fontSize: '14px',
        color: TEXT_COLOR,
        backgroundColor: '#FFFFFF',
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: BORDER_COLOR,
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
          borderColor: BORDER_COLOR,
        },
      }}
    >
      {SORT_OPTIONS.map(o => (
        <MenuItem key={o.value} value={o.value}>
          Sort by {o.label}
        </MenuItem>
      ))}
    </Select>
  );

  if (isMobile) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        gap="12px"
        sx={{ width: '100%' }}
      >
        <PeopleSearchInput value={search} onChange={onSearchChange} fullWidth />
        <Box display="flex" gap="12px" alignItems="center">
          <Box flex={1}>{sortSelect}</Box>
          <AnalyticsDateFilterTrigger
            preset={dateFilterPreset}
            startDate={dateFilterStartDate}
            endDate={dateFilterEndDate}
            onClick={onOpenDateFilter}
            sx={{
              width: 56,
              height: 50,
              justifyContent: 'center',
              borderRadius: '8px',
              '& .MuiTypography-root': { display: 'none' },
            }}
          />
          <ExportButton
            onClick={onExport}
            loading={exporting}
            label="Export"
            hideLabelOnMobile
          />
        </Box>
      </Box>
    );
  }

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      sx={{ width: '100%', gap: '16px' }}
    >
      <Box display="flex" gap="16px" alignItems="center">
        <AnalyticsDateFilterTrigger
          preset={dateFilterPreset}
          startDate={dateFilterStartDate}
          endDate={dateFilterEndDate}
          onClick={onOpenDateFilter}
          sx={{
            width: 250,
            height: 50,
            borderRadius: '8px',
          }}
        />
        {sortSelect}
        <ExportButton
          onClick={onExport}
          loading={exporting}
          label="Export"
        />
      </Box>
      <PeopleSearchInput value={search} onChange={onSearchChange} />
    </Box>
  );
}
